import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import { loginSerializer } from './login/loginSerializer';
import { registrationSerializer } from './registration/registrationSerializer';
import { updateSerializer } from './update/updateSerializer';
import Commons from '@features/shared/libraries/commons';
import { notificationService } from '@features/services/notificationService';

let refreshTimeout = null;

function refreshUserData() {
  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;
  console.log("🚀 ~ refreshUserData ~ currentUser:", currentUser)

  if (!currentUser?.id) {
    return;
  }

  if (currentUser?.metadata?.service !== constants.AUTH.SERVICE_NAME) {
    bus.emit(constants.EVENTS.USER.LOGOUT);
    return;
  }

  const requestId = `refreshUserData-${Date.now()}`;
  let url = `${constants.ENDPOINTS.USER.REFRESH_DATA}`;
  url = url.replace('{userId}', currentUser.id);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url,
    method: 'GET',
    data: {}
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);

      const parsed = updateSerializer.deserialize(res.data || res.error);

      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.REFRESH_DATA_SUCCESS, parsed.user);
      } else if (
        res.data?.statusCode === 404 ||
        res.error?.statusCode === 404
      ) {
        bus.emit(constants.EVENTS.USER.LOGOUT);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Compte supprimé',
          body: "Votre compte n'existe plus. Vous avez été déconnecté."
        });

        setTimeout(() => {
          window.location.reload();
        }, 5000);
      } else {
        bus.emit(constants.EVENTS.USER.REFRESH_DATA_ERROR, parsed);
      }
    }
  };
  bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
}

function startAutoRefresh() {
  if (refreshTimeout) {
    clearInterval(refreshTimeout);
  }

  refreshUserData();

  refreshTimeout = setInterval(() => {
    refreshUserData();
  }, 5 * 60 * 1000);
}

function stopAutoRefresh() {
  if (refreshTimeout) {
    clearInterval(refreshTimeout);
    refreshTimeout = null;
  }
}

async function syncDeviceToken(userId) {
  if (!userId) return;

  try {
    const token = await Commons.getDeviceToken();
    console.log("🚀 ~ syncDeviceToken ~ token:", token)
    if (token) {
      console.log('[Auth] Device token identified, syncing...');
      const userStore = getStore('user');
      const countryCode = userStore?.state?.current?.countryCode || 'ANY';
      await notificationService.uploadDeviceToken(userId, token, countryCode);
    } else {
      console.log('[Auth] No device token found (browser or permission denied)');
    }
  } catch (error) {
    console.error('[Auth] Failed to sync device token:', error);
  }
}

bus.on(constants.EVENTS.USER.LOGIN, (formData) => {
  const reqData = loginSerializer.serialize(formData);
  const requestId = `login-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${constants.ENDPOINTS.USER.LOGIN}`,
    method: 'POST',
    data: reqData
  });

  bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = loginSerializer.deserialize(res.data || res.error);
      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.LOGIN_SUCCESS, parsed.user);
        if (formData.updateAfterLogin) {
          bus.emit(constants.EVENTS.USER.UPDATE, formData);
        }
      } else {
        bus.emit(constants.EVENTS.USER.LOGIN_ERROR, res);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: parsed.errorTitle,
          body: parsed.message
        });
      }
    }
  });
});

bus.on(constants.EVENTS.USER.REGISTER, (formData) => {
  const reqData = registrationSerializer.serialize(formData);
  const requestId = `register-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${constants.ENDPOINTS.USER.REGISTER}`,
    method: 'POST',
    data: reqData
  });

  bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      const parsed = registrationSerializer.deserialize(res.data || res.error);
      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.REGISTER_SUCCESS, parsed.user);
      } else {
        if (parsed.alreadyExists && formData.tryLoginOnExist) {
          bus.emit(constants.EVENTS.USER.LOGIN, formData);
          return;
        }
        bus.emit(constants.EVENTS.USER.REGISTER_ERROR, res);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: parsed.errorTitle,
          body: parsed.message
        });
      }
    }
  });
});

bus.on(constants.EVENTS.USER.UPDATE, (updateData) => {
  console.log('🚀 ~ updateData:', updateData);
  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;

  const email = updateData.email || currentUser?.email;

  if (!email) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Aucun email fourni pour la mise à jour'
    });
    bus.emit(constants.EVENTS.USER.UPDATE_ERROR, {
      message: 'Aucun email fourni'
    });
    return;
  }

  const isGuestUpdate = !currentUser?.email && !!updateData.email;

  bus.emit(constants.EVENTS.LOADING.START);
  const reqData = updateSerializer.serialize({
    ...updateData,
    email
  });
  const requestId = `update-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${constants.ENDPOINTS.USER.UPDATE}`,
    method: 'POST',
    data: reqData
  });

  bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      bus.emit(constants.EVENTS.LOADING.FINISH);
      const parsed = updateSerializer.deserialize(res.data || res.error);
      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.UPDATE_SUCCESS, parsed.user);
      } else {
        bus.emit(constants.EVENTS.USER.UPDATE_ERROR, res);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: parsed.errorTitle,
          body: parsed.message
        });
      }
    }
  });
});

bus.on(constants.EVENTS.BOOKMAKER.BOOKMAKER_ID_CONFIRMED, (data) => {
  const { bookmakerId, bookmaker } = data;

  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;

  bus.emit(constants.EVENTS.USER.UPDATE, {
    metadata: {
      bookmakerIds: {
        ...(currentUser?.metadata?.bookmakerIds || {}),
        [bookmaker]: bookmakerId
      }
    }
  });
});

bus.on(constants.EVENTS.USER.LOGIN_SUCCESS, (user) => {
  startAutoRefresh();
  syncDeviceToken(user?.id);
});

bus.on(constants.EVENTS.USER.LOGOUT, () => {
  stopAutoRefresh();
});

bus.on(constants.EVENTS.USER.REFRESH_DATA, () => {
  refreshUserData();
});

bus.on(constants.EVENTS.APP.READY, () => {
  refreshUserData();
  const userStore = getStore('user');
  if (userStore?.state?.current?.id) {
    syncDeviceToken(userStore.state.current.id);
  }
});

